import { Lock, Save } from "lucide-react";
import { ActionButtonSpinner } from "./reimbursementActionButtonLoading";
import { cn } from "@/lib/utils/helpers";
import type { ReimbursementMilestoneStatus } from "../../utils/reimbursementMilestoneStatus";
import {
  isSectionSaveDisabled,
  isSectionSubmitDisabled,
  isSectionUnlockDisabled,
  shouldShowPmcSectionSaveSubmitActions,
  type ReimbursementSectionStatusValue,
} from "../../utils/reimbursementSectionStatus";

interface MilestoneSectionActionsProps {
  milestoneId: number;
  status: ReimbursementMilestoneStatus;
  sectionWorkflowStatus?: ReimbursementSectionStatusValue;
  /** When true, required fields/docs for this section are filled (uploaded, not underwriter-approved). */
  sectionSubmitReady?: boolean;
  saveDisabled?: boolean;
  saveLoading?: boolean;
  submitLoading?: boolean;
  onSaveDraft?: () => void;
  onSubmitClick?: () => void;
}

interface MilestoneUnlockActionProps {
  milestoneId: number;
  unlockDisabled?: boolean;
  unlockLoading?: boolean;
  onUnlock?: () => void;
}

export function MilestoneUnlockAction({
  milestoneId,
  unlockDisabled,
  unlockLoading,
  onUnlock,
}: MilestoneUnlockActionProps) {
  const disabled = Boolean(unlockDisabled) || Boolean(unlockLoading);

  return (
    <div className="mb-2 mt-2 flex w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={onUnlock}
        className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-[rgb(21_151_255)] bg-white px-3 text-sm font-semibold text-[rgb(21_151_255)] transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid={`reimbursement-status-bar-unlock-${milestoneId}`}
        aria-busy={unlockLoading}
      >
        {unlockLoading ? (
          <ActionButtonSpinner size={14} />
        ) : (
          <Lock size={14} aria-hidden="true" />
        )}
        {unlockLoading ? "Unlocking…" : "Unlock"}
      </button>
    </div>
  );
}

export function MilestoneSectionActions({
  milestoneId,
  status: _status,
  sectionWorkflowStatus = null,
  sectionSubmitReady = false,
  saveDisabled,
  saveLoading,
  submitLoading,
  onSaveDraft,
  onSubmitClick,
}: MilestoneSectionActionsProps) {
  if (!shouldShowPmcSectionSaveSubmitActions(sectionWorkflowStatus)) {
    return null;
  }

  const draftSaveDisabled =
    Boolean(saveDisabled) ||
    Boolean(saveLoading) ||
    Boolean(submitLoading) ||
    isSectionSaveDisabled(sectionWorkflowStatus);
  const submitDisabled =
    Boolean(saveDisabled) ||
    Boolean(saveLoading) ||
    Boolean(submitLoading) ||
    isSectionSubmitDisabled(sectionWorkflowStatus, sectionSubmitReady);

  return (
    <div className="mb-2 mt-2 grid w-full grid-cols-2 gap-2">
      <button
        type="button"
        disabled={draftSaveDisabled}
        onClick={onSaveDraft}
        className="flex h-8 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid={`reimbursement-status-bar-save-draft-${milestoneId}`}
        aria-busy={saveLoading}
      >
        {saveLoading ? (
          <ActionButtonSpinner size={12} />
        ) : (
          <Save size={12} className="shrink-0" aria-hidden="true" />
        )}
        <span className="truncate">{saveLoading ? "Saving…" : "Save as Draft"}</span>
      </button>
      <button
        type="button"
        disabled={submitDisabled}
        onClick={onSubmitClick}
        className={cn(
          "flex h-8 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2 text-xs font-semibold transition disabled:cursor-not-allowed",
          submitDisabled
            ? "bg-slate-100 text-slate-400"
            : "bg-gradient-to-b from-[rgb(44_160_255)] to-[rgb(12_128_220)] text-white hover:from-[#3b9ff7] hover:to-[#0b75c4]",
        )}
        data-testid={`reimbursement-status-bar-submit-${milestoneId}`}
        aria-busy={submitLoading}
      >
        {submitLoading ? <ActionButtonSpinner size={12} className="text-white" /> : null}
        <span className="truncate">{submitLoading ? "Submitting…" : "Submit"}</span>
      </button>
    </div>
  );
}

export interface ReimbursementMilestoneSectionActionRowProps {
  milestoneId: number;
  status: ReimbursementMilestoneStatus;
  sectionActionMode?: "pmc" | "liberty";
  sectionUnlocked?: Partial<Record<1 | 2 | 3, boolean>>;
  sectionWorkflowStatus?: ReimbursementSectionStatusValue;
  sectionSubmitReady?: boolean;
  saveDisabled?: boolean;
  sectionSaveLoading?: boolean;
  sectionActionLoading?: {
    milestoneId: number;
    kind: "submit" | "unlock";
  } | null;
  onSaveDraft?: () => void;
  onSubmitClick?: () => void;
  onUnlock?: () => void;
}

export function ReimbursementMilestoneSectionActionRow({
  milestoneId,
  status,
  sectionActionMode = "pmc",
  sectionUnlocked,
  sectionWorkflowStatus = null,
  sectionSubmitReady = false,
  saveDisabled,
  sectionSaveLoading,
  sectionActionLoading,
  onSaveDraft,
  onSubmitClick,
  onUnlock,
}: ReimbursementMilestoneSectionActionRowProps) {
  const unlockLoading =
    sectionActionLoading?.milestoneId === milestoneId &&
    sectionActionLoading.kind === "unlock";
  const submitLoading =
    sectionActionLoading?.milestoneId === milestoneId &&
    sectionActionLoading.kind === "submit";

  if (sectionActionMode === "liberty") {
    if (sectionWorkflowStatus !== "SUBMITTED") {
      return null;
    }
    return (
      <MilestoneUnlockAction
        milestoneId={milestoneId}
        unlockDisabled={
          sectionUnlocked?.[milestoneId as 1 | 2 | 3] ??
          isSectionUnlockDisabled(sectionWorkflowStatus, true)
        }
        unlockLoading={unlockLoading}
        onUnlock={onUnlock}
      />
    );
  }

  return (
    <MilestoneSectionActions
      milestoneId={milestoneId}
      status={status}
      sectionWorkflowStatus={sectionWorkflowStatus}
      sectionSubmitReady={sectionSubmitReady}
      saveDisabled={saveDisabled}
      saveLoading={sectionSaveLoading}
      submitLoading={submitLoading}
      onSaveDraft={onSaveDraft}
      onSubmitClick={onSubmitClick}
    />
  );
}
